// The three advisory lanes beyond merges (043): faction aliases, story
// filings, narrative links between incidents. Same contract as the merge
// advisor's queue in incidents.ts — the model may SAY, a human DECIDES, and
// nothing here writes anything until a curator clicks. Every chip's wording
// carries the maybe out loud, because a model's inference dressed as a fact
// is the failure the whole basis rule exists to prevent.

import { del, get, post } from './api';

export interface AliasSuggestionDto {
  id: string;
  alias: string;
  factionId: string;
  factionName: string;
  why: string;
  confidence: string | null;
  createdAt: string;
}

export interface FilingSuggestionDto {
  id: string;
  entityId: string;
  storyId: string;
  storyName: string;
  summary: string | null;
  why: string;
  confidence: string | null;
  createdAt: string;
}

export interface LinkSuggestionDto {
  id: string;
  sourceId: string;
  targetId: string;
  kind: 'answers' | 'counter_claim' | 'related';
  sourceTitle: string;
  targetTitle: string;
  why: string;
  confidence: string | null;
  createdAt: string;
}

export interface IncidentLinkDto {
  id: string;
  srcId: string;
  dstId: string;
  kind: 'answers' | 'counter_claim' | 'related';
  why: string | null;
  srcTitle: string;
  dstTitle: string;
}

export interface EquipmentSuggestionDto {
  id: string;
  name: string;
  class: string;
  why: string;
  evidenceCount: number;
  confidence: string | null;
  createdAt: string;
}

export interface GeoSuggestionDto {
  id: string;
  name: string;
  country: string | null;
  lat: number;
  lon: number;
  why: string;
  evidenceCount: number;
  confidence: string | null;
  createdAt: string;
}

/** A district changing hands, proposed by the live feed (migration 054).
 *  Deterministic — rules over resolved attributions, no model — but advice
 *  all the same: PREVIEWED on the map and applied by a curator, never by the
 *  server itself (the auto-apply setting is gone, 068). */
export interface ControlSuggestionDto {
  id: string;
  adm2Fid: number;
  districtName: string | null;
  country: string | null;
  countryIso: string;
  lon: number;
  lat: number;
  fromFaction: string | null;
  fromStatus: string | null;
  toFaction: string;
  toStatus: 'controlled' | 'contested';
  contestedBy: string | null;
  why: string;
  evidenceCount: number;
  channels: number;
  confidence: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AdviceDto {
  aliases: AliasSuggestionDto[];
  filings: FilingSuggestionDto[];
  links: LinkSuggestionDto[];
  incidentLinks: IncidentLinkDto[];
  equipment: EquipmentSuggestionDto[];
  geo: GeoSuggestionDto[];
  control: ControlSuggestionDto[];
}

let advice: AdviceDto = {
  aliases: [], filings: [], links: [], incidentLinks: [], equipment: [],
  geo: [], control: [],
};

export async function loadAdvice(): Promise<void> {
  try {
    const dto = await get<AdviceDto>('/api/advice');
    // Every lane defaulted: a server that predates a lane must not blank the
    // arrays the callers iterate.
    advice = {
      aliases: dto.aliases ?? [], filings: dto.filings ?? [],
      links: dto.links ?? [], incidentLinks: dto.incidentLinks ?? [],
      equipment: dto.equipment ?? [], geo: dto.geo ?? [],
      control: dto.control ?? [],
    };
  } catch {
    // A failed load keeps the last known queues; every chip is advisory only.
  }
}

/** Open district-reassignment proposals — a GLOBAL subject like equipment
 *  and geo, so the Intel pane renders these. */
export function controlSuggestions(): ControlSuggestionDto[] {
  return advice.control;
}

/** GET /api/advice/control/{id}/preview: what Apply would write, with
 *  nothing written — the planned outline, who loses what, and the reports
 *  behind it (those the caller can read). */
export interface ControlPreviewDto {
  id: string;
  plan: {
    geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
    km2: number; outsideKm2: number; unheldKm2: number; absorbedKm2: number;
    countryIso: string; country: string;
    takes: { id: string; name: string | null; faction: string | null; status: string | null;
             contestedBy: string | null; km2: number }[];
  };
  reports: { id: string; name: string | null; kind: string | null; summary: string | null;
             attributedTo: string | null; basis: string | null; channels: string[];
             at: string | null; lon: number | null; lat: number | null }[];
}

export async function previewControl(id: string): Promise<ControlPreviewDto> {
  return get<ControlPreviewDto>(`/api/advice/control/${id}/preview`);
}

/** Reassign the district as proposed. Returns the server's summary, whose
 *  `rev` is what bumps the tiles. */
export async function applyControl(id: string): Promise<{ rev: number | null }> {
  const res = await post<{ rev: number | null }>(`/api/advice/control/${id}/apply`);
  advice.control = advice.control.filter((c) => c.id !== id);
  return res;
}

/** Open "unlocatable place" proposals — like equipment, a GLOBAL subject, so
 *  the Intel pane renders these rather than a group card. */
export function geoSuggestions(): GeoSuggestionDto[] {
  return advice.geo;
}

/** Teach the geocoder the proposed coordinate. Future reports of the name
 *  resolve; stored events keep what they got. */
export async function applyGeo(id: string): Promise<void> {
  await post(`/api/advice/geo/${id}/apply`);
  advice.geo = advice.geo.filter((g) => g.id !== id);
}

/** Open "possible new system" proposals — a GLOBAL subject, unlike the other
 *  lanes, so the Intel pane renders these rather than a group card. */
export function equipmentSuggestions(): EquipmentSuggestionDto[] {
  return advice.equipment;
}

/** Draft a type definition for a proposed system. Leaves the row open: the
 *  suggestion is answered by `applyEquipment` once the type actually exists. */
export async function researchEquipment(id: string): Promise<{
  name: string; draft: Record<string, unknown>;
}> {
  return post(`/api/advice/equipment/${id}/research`);
}

export async function applyEquipment(id: string): Promise<void> {
  await post(`/api/advice/equipment/${id}/apply`);
  advice.equipment = advice.equipment.filter((e) => e.id !== id);
}

/** Alias suggestions whose unresolved name appears in `names` (the unresolved
 *  attribution names carried by a card's members), case-insensitively. */
export function aliasSuggestionsFor(names: Set<string>): AliasSuggestionDto[] {
  if (!names.size) return [];
  const lower = new Set([...names].map((n) => n.toLowerCase()));
  return advice.aliases.filter((a) => lower.has(a.alias.toLowerCase()));
}

/** The open filing proposal for any of these events, if one exists. */
export function filingFor(entityIds: Set<string>): FilingSuggestionDto | null {
  return advice.filings.find((f) => entityIds.has(f.entityId)) ?? null;
}

/** Open link suggestions naming this incident, either side. */
export function linkSuggestionsFor(incidentId: string): LinkSuggestionDto[] {
  return advice.links.filter(
    (s) => s.sourceId === incidentId || s.targetId === incidentId);
}

/** Applied (curated) links touching this incident. */
export function incidentLinksFor(incidentId: string): IncidentLinkDto[] {
  return advice.incidentLinks.filter(
    (l) => l.srcId === incidentId || l.dstId === incidentId);
}

export async function applyAdvice(lane: 'aliases' | 'filings' | 'links',
                                  id: string): Promise<void> {
  await post(`/api/advice/${lane}/${id}/apply`);
  await loadAdvice();
}

export async function dismissAdvice(
    lane: 'aliases' | 'filings' | 'links' | 'equipment' | 'geo' | 'control',
    id: string): Promise<void> {
  await post(`/api/advice/${lane}/${id}/dismiss`);
  advice[lane] = (advice[lane] as { id: string }[])
    .filter((s) => s.id !== id) as never;
}

export async function removeIncidentLink(id: string): Promise<void> {
  await del(`/api/advice/incident-links/${id}`);
  advice.incidentLinks = advice.incidentLinks.filter((l) => l.id !== id);
}

/** The link kinds in words, from the SOURCE side ("a answers b") and the
 *  TARGET side ("b is answered by a") — the direction must survive rendering,
 *  or "X disputes Y" reads as "Y disputes X" on Y's own card. */
export function linkWording(kind: LinkSuggestionDto['kind'],
                            fromSource: boolean,
                            applied: boolean): string {
  const table: Record<LinkSuggestionDto['kind'], [string, string]> = {
    answers: applied ? ['answers', 'answered by'] : ['may answer', 'may be answered by'],
    counter_claim: applied ? ['disputes the account of', 'account disputed by']
      : ['may dispute the account of', 'account may be disputed by'],
    related: applied ? ['related to', 'related to']
      : ['may be part of the same wave as', 'may be part of the same wave as'],
  };
  const [fromSrc, fromDst] = table[kind];
  return fromSource ? fromSrc : fromDst;
}
